import { toolKey, toolLogos, uniqueTools } from "components/Screens/Marketplace/Customer/utils/tools";
import { isWithheld } from "components/Screens/Marketplace/Customer/utils/redaction";
import type {
  TalentProfile,
  WorkExperienceItem,
  EducationItem,
  CertificationItem,
} from "components/Screens/Marketplace/Customer/types";

export function mapTalentToOverviewItems(talent: TalentProfile) {
  const totalExperience = talent.overview?.totalYearsOfExperience || 0;
  const educationLevel =
    talent.overview?.highestEducation ||
    (talent.education && talent.education.length > 0 ? talent.education[0].level : "");

  const languages = (talent.overview?.languagesSpoken || []).join(", ");

  const items: { icon: string; label: string; value: string }[] = [];

  if (languages) {
    items.push({ icon: "languages-spoken", label: "Languages spoken", value: languages });
  }

  if (totalExperience > 0) {
    items.push({ icon: "work-experience", label: "Work experience", value: `${totalExperience}+ years` });
  }

  if (educationLevel) {
    items.push({ icon: "highest-education", label: "Highest education", value: educationLevel });
  }


  return items;
}

function parseDateOnly(value: string): Date {
  const [datePart] = String(value).split("T");
  const [y, m, d] = datePart.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1); // local midnight, no conversion
}

export function mapTalentToWorkExperience(talent: TalentProfile): WorkExperienceItem[] {
  return (talent.experiences || [])
    .filter((exp) => !!exp.startDate)
    .map((exp) => {
      const startDate = parseDateOnly(exp.startDate!);
      const endDate = exp.endDate ? parseDateOnly(exp.endDate) : null;

      const startMonth = startDate.toLocaleDateString("en-US", { month: "short" });
      const startYear = startDate.getFullYear();
      const dateRange = endDate
        ? `${startMonth} ${startYear} - ${endDate.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          })}`
        : `${startMonth} ${startYear} - Present`;

      const rawAchievements = exp.achievements as string | string[] | undefined;
      let responsibilities: string[];
      if (Array.isArray(rawAchievements)) {
        responsibilities = rawAchievements;
      } else if (typeof rawAchievements === "string" && rawAchievements.trim()) {
        responsibilities = [rawAchievements];
      } else {
        responsibilities = [];
      }

      const companyWithheld = !exp.company && isWithheld(talent, "employers");
      return {
        dateRange,
        jobTitle: exp.title || "Untitled",
        companyName: exp.company || (companyWithheld ? "" : "Unknown"),
        companyWithheld,
        isEdgeCustomer: !!exp.customerId,
        responsibilities,
      };
    });
}

export function mapTalentToEducation(talent: TalentProfile): EducationItem[] {
  return (talent.education || []).map((edu) => {
    const startYear = edu.startDate ? new Date(edu.startDate).getFullYear() : "";
    const endYear = edu.endDate ? new Date(edu.endDate).getFullYear() : "";
    let dateRange = "";
    if (startYear && endYear) dateRange = `${startYear} - ${endYear}`;
    else if (startYear) dateRange = `${startYear} - Present`;

    const degree =
      edu.title || (edu.level && edu.fieldOfStudy ? `${edu.level} of ${edu.fieldOfStudy}` : edu.level || "");

    const institutionWithheld = !edu.institution && isWithheld(talent, "institutions");
    return {
      dateRange,
      degree,
      institution: edu.institution || (institutionWithheld ? "" : "Unknown"),
      institutionWithheld,
    };
  });
}

export interface SoftwareSkill {
  name: string;
  logoUrl?: string;
}

/** Every tool once with its logo, as the talent network showed it before PLG (product 2026-10-24). */
export function mapTalentToSoftwareSkills(talent: TalentProfile): SoftwareSkill[] {
  const tools = talent.tools || [];
  const logos = toolLogos(tools);
  return uniqueTools(tools.map((tool) => tool.name || "")).map((name) => {
    const logoUrl = logos.get(toolKey(name));
    return logoUrl ? { name, logoUrl } : { name };
  });
}

export function mapTalentToSkillsChips(talent: TalentProfile) {
  return (talent.skills || []).map((skill) => skill.name);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * "Issued Aug 2026" (prototype fmtMonth). The profile sends issuedDate (QA PO-5); Edge certifications usually have
 * only endDate, when they were completed, so that comes next. startDate is the last resort.
 */
function issuedLabel(date?: string): string {
  const m = typeof date === "string" ? /^(\d{4})-(\d{2})/.exec(date) : null;
  return m && +m[2] >= 1 && +m[2] <= 12 ? `Issued ${MONTHS[+m[2] - 1]} ${m[1]}` : "Date not specified";
}

export function mapTalentToCertifications(talent: TalentProfile): CertificationItem[] {
  return (talent.certifications || []).map((cert) => ({
    title: cert.title || "Certification",
    issuedDate: issuedLabel(cert.issuedDate || cert.endDate || cert.startDate),
    isEdgeEdu: cert.isEdge || false,
    description: cert.isEdge ? cert.certDescriptionForCustomer : undefined,
  }));
}

export function generateBioFromTalent(talent: TalentProfile) {
  if (talent.bio) {
    return talent.bio;
  }
  return "";
}
